/** Local continuity checks, never signature verification or cloud authority. */
export interface RememberedSession {
  ownerId: string;
  sessionId: string;
  refreshToken: string;
  expiresAt: number;
  accessToken: string;
  raw: string;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const object = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

export function sdkSessionStorageKey(projectUrl: string): string {
  // SupabaseClient's existing default. No key is renamed or newly persisted.
  return `sb-${new URL(projectUrl).hostname.split('.')[0]}-auth-token`;
}

export function parseRememberedSession(raw: string | null, projectUrl: string): RememberedSession | null {
  try {
    if (!raw || raw.length > 100_000) return null;
    const session: unknown = JSON.parse(raw);
    if (!object(session) || !object(session.user)) return null;
    const ownerId = session.user.id;
    if (typeof ownerId !== 'string' || !UUID.test(ownerId) ||
        session.user.aud !== 'authenticated' || session.user.is_anonymous === true ||
        session.token_type !== 'bearer' || typeof session.refresh_token !== 'string' ||
        session.refresh_token.trim() === '' || typeof session.access_token !== 'string' ||
        typeof session.expires_at !== 'number' || !Number.isSafeInteger(session.expires_at) ||
        session.expires_at <= 0) return null;
    const parts = session.access_token.split('.');
    if (parts.length !== 3 || parts.some((part) => !part)) return null;
    const decode = (part: string): unknown => JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/')));
    const header = decode(parts[0]);
    const claims = decode(parts[1]);
    if (!object(header) || header.typ !== 'JWT' ||
        !['HS256', 'ES256', 'RS256'].includes(String(header.alg)) || !object(claims)) return null;
    const audience = claims.aud;
    if (claims.sub !== ownerId || claims.role !== 'authenticated' ||
        !(audience === 'authenticated' || (Array.isArray(audience) && audience.includes('authenticated'))) ||
        claims.iss !== `${projectUrl.replace(/\/$/, '')}/auth/v1` ||
        claims.exp !== session.expires_at ||
        typeof claims.session_id !== 'string' || !UUID.test(claims.session_id)) return null;
    return { ownerId, sessionId: claims.session_id, refreshToken: session.refresh_token, expiresAt: session.expires_at, accessToken: session.access_token, raw };
  } catch {
    return null;
  }
}

export type RememberedSessionRead =
  | { kind: 'present'; session: RememberedSession }
  | { kind: 'missing' }
  | { kind: 'malformed' }
  | { kind: 'unreadable' };

/** Missing SDK storage is distinct from a failed or incoherent read. */
export function readRememberedSession(readRaw: () => string | null, projectUrl: string): RememberedSessionRead {
  let raw: string | null;
  try { raw = readRaw(); } catch { return { kind: 'unreadable' }; }
  if (raw === null) return { kind: 'missing' };
  const session = parseRememberedSession(raw, projectUrl);
  return session ? { kind: 'present', session } : { kind: 'malformed' };
}

/** Only an awaited service refresh result may supply this value, never an event. */
export function matchesRefreshedSession(
  expected: Pick<RememberedSession, 'ownerId' | 'sessionId'>,
  result: unknown,
  current: RememberedSessionRead,
  projectUrl: string,
  now = Date.now(),
): boolean {
  try {
    if (current.kind !== 'present' || !object(result) || result.error !== null ||
        !object(result.data) || !object(result.data.session) || !object(result.data.user)) return false;
    const raw = JSON.stringify(result.data.session);
    const returned = parseRememberedSession(raw, projectUrl);
    return returned !== null && returned.ownerId === expected.ownerId &&
      returned.sessionId === expected.sessionId && returned.expiresAt * 1000 > now &&
      result.data.user.id === expected.ownerId && result.data.user.aud === 'authenticated' &&
      result.data.user.is_anonymous !== true && current.session.raw === raw;
  } catch { return false; }
}

export function isRefreshRequest(input: RequestInfo | URL, init: RequestInit | undefined, projectUrl: string): boolean {
  try {
    const request = typeof Request !== 'undefined' && input instanceof Request ? input : null;
    const url = new URL(request ? request.url : String(input));
    const target = new URL(`${projectUrl.replace(/\/$/, '')}/auth/v1/token`);
    return (init?.method ?? request?.method ?? 'GET').toUpperCase() === 'POST' &&
      url.origin === target.origin && url.pathname === target.pathname &&
      url.username === '' && url.password === '' && url.hash === '' &&
      url.searchParams.getAll('grant_type').length === 1 &&
      url.searchParams.get('grant_type') === 'refresh_token';
  } catch {
    return false;
  }
}

export type RefreshObservation =
  | { kind: 'response'; status: number }
  | { kind: 'connection-failure' }
  | { kind: 'other-error' };

/** No body reads, option changes, retries or logging; SDK results keep identity. */
export function observeRefreshFetch<T>(
  fetcher: typeof fetch,
  projectUrl: string,
  capture: () => T | null,
  observed: (ticket: T, outcome: RefreshObservation) => void,
): typeof fetch {
  return async (input, init) => {
    let ticket: T | null = null;
    try {
      if (isRefreshRequest(input, init, projectUrl)) ticket = capture();
    } catch { /* Observation must not change SDK transport. */ }
    const notify = (outcome: RefreshObservation) => {
      try { if (ticket !== null) observed(ticket, outcome); } catch { /* Same boundary. */ }
    };
    try {
      const response = await fetcher(input, init);
      notify({ kind: 'response', status: response.status });
      return response;
    } catch (error) {
      notify({ kind: error instanceof TypeError ? 'connection-failure' : 'other-error' });
      throw error;
    }
  };
}
